import type { PartnerProgramServiceFormValue } from '../types/partnerProgramService';
import type { PartnerProgramFaqFormValue } from '../types/partnerProgramFaq';

type BuildPartnerBrandingOptions = {
  partnerLogoUrl?: string;
  displayName?: string;
  partnerProgramServices?: PartnerProgramServiceFormValue[];
  partnerProgramFaqs?: PartnerProgramFaqFormValue[];
};

function normalizePartnerProgramServices(
  services?: PartnerProgramServiceFormValue[],
): PartnerProgramServiceFormValue[] | undefined {
  if (services === undefined) return undefined;

  return services
    .filter((service) => service?.title?.trim() && service?.image?.trim())
    .map((service, index) => ({
      id: service.id?.trim() || `service-${index}`,
      title: service.title.trim(),
      image: service.image.trim(),
      href: service.href?.trim() || undefined,
      iconKey: service.iconKey?.trim() || 'bookUser',
      sortOrder: index,
      isActive: service.isActive !== false,
    }));
}

function normalizePartnerProgramFaqs(
  faqs?: PartnerProgramFaqFormValue[],
): PartnerProgramFaqFormValue[] | undefined {
  if (faqs === undefined) return undefined;

  return faqs
    .filter((faq) => faq?.question?.trim() && faq?.answer?.trim())
    .map((faq, index) => ({
      id: faq.id?.trim() || `faq-${index}`,
      question: faq.question.trim(),
      answer: faq.answer.trim(),
      sortOrder: index,
      isActive: faq.isActive !== false,
    }));
}

export function buildPartnerBrandingPayload({
  partnerLogoUrl,
  displayName,
  partnerProgramServices,
  partnerProgramFaqs,
}: BuildPartnerBrandingOptions) {
  const payload: Record<string, unknown> = {};

  if (partnerLogoUrl !== undefined) {
    payload.logos = {
      partner: partnerLogoUrl.trim(),
      partnerAlt: displayName ? `${displayName} logo` : 'Partner logo',
    };
  }

  const normalizedServices = normalizePartnerProgramServices(partnerProgramServices);
  if (normalizedServices !== undefined) {
    payload.partnerProgramServices = normalizedServices;
  }

  const normalizedFaqs = normalizePartnerProgramFaqs(partnerProgramFaqs);
  if (normalizedFaqs !== undefined) {
    payload.partnerProgramFaqs = normalizedFaqs;
  }

  if (Object.keys(payload).length === 0) {
    return undefined;
  }

  return payload;
}

export function getPartnerProgramFaqsForForm(
  branding?: {
    partnerProgramFaqs?: PartnerProgramFaqFormValue[] | null;
  } | null,
): PartnerProgramFaqFormValue[] {
  if (branding?.partnerProgramFaqs?.length) {
    return branding.partnerProgramFaqs.map((faq) => ({
      ...faq,
      isActive: faq.isActive !== false,
    }));
  }

  return [];
}

export function getPartnerProgramServicesForForm(
  branding?: {
    partnerProgramServices?: PartnerProgramServiceFormValue[] | null;
    popularServices?: PartnerProgramServiceFormValue[] | null;
  } | null,
): PartnerProgramServiceFormValue[] {
  const savedServices =
    branding?.partnerProgramServices?.length
      ? branding.partnerProgramServices
      : branding?.popularServices;

  if (savedServices?.length) {
    return savedServices.map((service) => ({
      ...service,
      isActive: service.isActive !== false,
    }));
  }

  return [];
}
